/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    1) Do not import any classes from the moment.js library (e.g. with
       import { moment } from 'moment'). This is becausethe moment.js classes can
       be very large and webpack has issues compressing them properly.
    2) Instead import the moment.js classes you need via HTML <script> tags.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import helper classes individually and not via the EzHelpers
    module. This is because the EzHelpers module imports all helper classes and can
    cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import Enumeration classes individually and not via the
    EzEnumerations module. This is because the EzEnumerations module imports all
    enumeration classes and can cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following into this class to avoid circular
    dependencies:
        import { EzApi } from '/public/common/javascript/ezapi.js'
        import { EzUI } from '/public/common/javascript/ezui.js'
        import { ezUI } from '/public/common/javascript/ezui.js'
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following helper classes into this class to avoid
    circular dependencies:
        import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
        import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
        import { EzFloat } from '/ezlibrary/helpers/EzFloat.js';
        import { EzChar } from '/ezlibrary/helpers/EzChar.js';
        import { EzString } from '/ezlibrary/helpers/EzString.js';
        import { EzStringConstants } from '/ezlibrary/helpers/EzStringConstants.js';
        import { EzArray } from '/ezlibrary/helpers/EzArray.js';
        import { EzMap } from '/ezlibrary/helpers/EzMap.js';
        import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
        import { EzError } from '/ezlibrary/helpers/EzError.js';
        import {
            DAY_IN_MONTH,
            EzDays
        } from '/ezlibrary/helpers/EzDays.js';
        import { EzJson } from '/ezlibrary/helpers/EzJson.js';
        import { EzPromise } from '/ezlibrary/helpers/EzPromise.js';
        import { EzAsync } from '/ezlibrary/helpers/EzAsync.js';
        import { EzUrl } from '/ezlibrary/helpers/EzUrl.js';
        import { EzDocument } from '/ezlibrary/helpers/EzDocument.js';
        import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
        import { EzHtmlCharacterCode } from '/ezlibrary/helpers/EzHtmlCharacterCode.js';
        import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
        import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
        import { EzDateTime } from '/ezlibrary/helpers/EzDateTime.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import {
    EzBadStateException,
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';

/**
 * @class
 * @extends {EzStaticClass}
 * @description
 * Static class that provides utility methods and properties for Objects
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzObject } from '/ezlibrary/helpers/EzObject.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Access via static properties and methods only:
 *      EzObject.{property|field|method}
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzObject extends EzStaticClass {
    /**
     * @static
     * @public @readonly @property
     * Returns an empty object
     * @returns {object}
     */
    static get EMPTY() {
        return new Object();
    }

    /**
     * @static
     * @public @method
     * Gets the values of all keys/properties of the provided object.
     * If the provided aObject is undefined or null an empty array is returned.
     * @param {any} aObject
     * @returns {array}
     * Array of the key/property names on the provided object.
     */
    static ezKeys(aObject) {
        return EzObject.isValid(aObject)
            ? Object.keys(aObject)
            : [];
    }

    /**
     * @static
     * @public @method
     * Gets the keys (aka property names) of the provided aObject.
     * If the provided aObject is undefined or null an empty array is returned.
     * @param {any} aObject
     * @returns {array}
     * Array of values for all keys/properties on the provided object.
     */
    static ezValues(aObject) {
        return EzObject.isValid(aObject)
            ? Object.values(aObject)
            : [];
    }

    /**
     * @static
     * @public @method
     * Determines if the instance associated with the provided ezApiName class is registered with
     * ezApi and available for use.
     * @param {any|string} classRefOrEzApiName
     * A class reference with a static ezApiName property OR the string ezApiName value of a class.
     * @returns {boolean}
     */
    static isAvailableFromEzApi(classRefOrEzApiName) {
        if (!globalThis.ezApi?.ezclocker) {
            throw new EzBadStateException(
                'Expecting a valid, not undefined, not null globalThis.ezApi.ezclocker reference.',
                'However, either globalThis.ezApi or globalThis.ezApi.ezclocker is not yet available.',
                EzObject,
                EzObject.isAvailableFromEzApi);
        }

        if (undefined === classRefOrEzApiName || null === classRefOrEzApiName) {
            return false;
        }

        const ezApiName = 'string' === typeof classRefOrEzApiName
            ? classRefOrEzApiName
            : classRefOrEzApiName?.ezApiName;

        return undefined !== ezApiName && null !== ezApiName && 0 !== ezApiName.length
            ? globalThis.ezApi.ezclocker?.[ezApiName]?.ready
            : false;
    }

    /**
     * @static
     * @public @function
     * Assigns the provided valueToAssignIfValid to the property on classInstance named propertyName IF
     * the provided valueToAssignIfValid is not undefined and not null.
     * Otherwise, does not perform the assignment and leaves the current value of the property as is.
     * @param {any} valueToAssignIfValid
     * @param {string} propertyName
     * @param {object} classInstance
     */
    static assignToOrIgnore(valueToAssignIfValid, propertyName, classInstance) {
        if (!EzObject.isValid(propertyName) || 'string' !== typeof propertyName || 0 === propertyName.length) {
            throw new EzBadParamException(
                { propertyName },
                EzObject,
                EzObject.assignToOrIgnore);
        }
        if (!EzObject.isValid(classInstance) || !classInstance?.[propertyname]) {
            throw new EzBadParamException(
                { classInstance },
                EzObject,
                EzObject.assignToOrIgnore);
        }

        if (!EzObject.isValid(valueToAssignIfValid)) {
            // Do nothing
            return;
        }

        classInstance[propertyName] = valueToAssignIfValid;
    }

    /**
     * @static
     * @public @method
     * Returns type information from Object.prototype.toString.call(aObject);
     * Result format
     * [object {type}]
     * @param {any} aObject
     * @returns {string}
     */
    static getObjectType(clazzNameOrClazz) {
        if (!EzObject.isValid(clazzNameOrClazz)) {
            throw new EzBadParamException(
                'clazzNameOrClazz',
                EzObject,
                EzObject.getObjectType);
        }

        if ('string' === typeof clazzNameOrClazz) {
            return clazzNameOrClazz;
        }

        return Object.prototype.toString.call(clazzNameOrClazz);
    }

    /**
     * @static
     * @public @method
     * Returns the name of the class that the provided value was constructed from.
     * If the value is undefined then undefined is returned.
     * If the value is null then null is returned.
     * If the value has a constructor then the constructor name is returned.
     * Otherwise, the Object.prototype.toString() is returned.
     * @param {any} clazz
     * @returns {string}
     */
    static getClassName(clazz) {
        if (!clazz) {
            throw new EzBadParamException(
                'clazz',
                EzObject,
                EzObject.getClassName);
        }

        if (clazz?.constructor) {
            return 'Function' === clazz.constructor.name && clazz?.name
                ? clazz?.name
                : clazz.constructor.name;
        }

        return EzObject.getObjectType(clazz);
    }

    /**
     * @static
     * @public @method
     * Attempts to determine if the provided instance was created from a class named clazzName.
     * In the case of instances that return as 'Function' or '[object Function'] a warning
     * is displayed indicating that the expectedClassNameOrInstance might not match.
     * @param {any} instance
     * @param {object|string} expectedClassNameOrInstance
     * The name of the expected class or an instance of the expected class object.
     * @param {undefined|null|boolean} ignoreCase
     * Defualt: false
     */
    static isInstanceOfClassName(instance, expectedClassNameOrInstance, ignoreCase = false) {
        if (!EzObject.isValid(expectedClassNameOrInstance)) {
            throw new EzBadParamException(
                'expectedClassNameOrInstance',
                EzObject,
                EzObject.isInstanceOfClassName);
        }

        const clazzName = 'string' === typeof expectedClassNameOrInstance
            ? expectedClassNameOrInstance
            : EzObject.getClassName(expectedClassNameOrInstance);

        if (0 === clazzName.length) {
            throw new EzBadParamException(
                'expectedClassNameOrInstance',
                EzObject,
                EzObject.isInstanceOfClassName);
        }

        if (!EzObject.isValid(instance)) {
            globalThis.console.error(
                'Call to EzObject.isInstanceOfClassName(...) returning false due to a failure to ' +
                'properly determine the class name of the provided instance. ' +
                `[Provided class name: ${clazzName}]` +
                `[Provided Instance toString: ${instance.toString()}] ` +
                `[Provided Instance JSON.stringify: JSON.stringify(instance))]` +
                `[Compare would ignore class name character case: ${ignoreCase}]`);

            return false;
        }

        const objectTypeName = EzObject.getObjectType(instance);

        const instanceClassName = 0 !== instance.length
            ? EzObject.getClassName(instance)
            : '';

        if (0 === instanceClassName.length) {
            globalThis.console.error(
                'Call to EzObject.isInstanceOfClassName(...) returning false due to a failure to ' +
                'properly determine the class name of the provided instance. ' +
                `[Provided class name: ${clazzName}]` +
                `[Provided Instance toString: ${instance.toString()}] ` +
                `[Provided Instance JSON.stringify: JSON.stringify(instance))]` +
                `[Compare would ignore class name character case: ${ignoreCase}]`);

            return false;
        }

        if ('function' === objectTypeName.toLowerCase() || '[object function]' === objectTypeName.toLowerCase()) {
            globalThis.console.warn(
                'Call to EzObject.isInstanceOfClassName(...) could possibly return an incorrect result due to detecting ' +
                `the provided instance only as ${instanceClassName}. ` +
                `[Provided class name: ${clazzName}]` +
                `[Instance toString: ${instance.toString()}] ` +
                `[Instance JSON.stringify: JSON.stringify(instance))]` +
                `[Compare would ignore class name character case: ${ignoreCase}]`);
        }

        return ignoreCase
            ? clazzName.toUpperCase() === instanceClassName.toUpperCase()
            : clazzName === instanceClassName;
    }

    /**
     * @static
     * @public @method
     * Gets the provided object if the object is NOT undefined or null, otherwise, gets the provdied defaultObject otherwise.
     * @param {any} aObject
     * @param {any} defaultObject
     * @returns {any}}
     */
    static assignOrDefault(aObject, defaultObject) {
        return EzObject.isValid(aObject)
            ? aObject
            : defaultObject;
    }

    /**
     * @static
     * @public @method
     * Gets the object if it is not undefined or null. Otherwise, returns undefined.
     * @param {any} aObject
     * @returns {undefined|object}
     */
    static assignOrUndefined(aObject) {
        return EzObject.assignOrDefault(aObject, undefined);
    }

    /**
     * @static
     * @public @method
     * Gets the provided object if the object is not undefined or null.
     * Returns null otherwise.
     * @param {any} aObject
     * @returns {object|null|any}
     */
    static assignOrNull(aObject) {
        return EzObject.isValid(aObject)
            ? aObject
            : null;
    }

    /**
     * @static
     * @public @method
     * Gets the provided object if the object is not undefined or null, otherwise,
     * returns an empty object.
     * @param {any} item
     * @returns {object}
     */
    static assignOrEmpty(aObject) {
        return EzObject.isValid(aObject)
            ? aObject
            : EzObject.EMPTY;
    }

    /**
     * @static
     * @public @method
     * Determines if the provided item is an object type.
     * @param {any} aObject
     * @returns {boolean}
     */
    static isObject(aObject) {
        return undefined !== aObject && null !== aObject && 'object' === typeof aObject;
    }

    /**
     * @static
     * @public @function
     * Determiens if the provided aObject equals undefined (and is not null or a valid instance)
     * @param {any} aObject
     * @returns {boolean}
     */
    static isUndefined(aObject) {
        return (undefined === aObject || 'undefined' === typeof aObject) && null !== aObject;
    }

    /**
     * @static
     * @public @function
     * Determines if the provided aObject is equal to null (and is not undefined or a valid instance).
     * @param {any} aObject
     * @returns {boolean}
     */
    static isNull(aObject) {
        return (null === aObject) && (undefined !== aObject && 'undefined' !== typeof aObject);
    }

    /**
     * @static
     * @public @method
     * Determiens if the provided item is not null AND not 'undefined'.
     * @param {any} aObject
     * @returns {boolean}
     */
    static isValid(aObject) {
        return undefined !== aObject && null != aObject;
    }

    /**
     * @static
     * @public @method
     * Evaluates all the provided arguments (if any) to be valid
     * @param {any} args
     * One or more arguments
     * @returns {boolean}
     */
    static allValid(...args) {
        for (const aIndex in args) {
            if (!EzObject.isValid(args[aIndex])) {
                return false;
            }
        }

        return true;
    }

    /**
     * @static
     * @public @method
     * Determines if any of the passed arguments are not valid
     * @param {any} args
     * One or more paramters
     * @returns {boolean}
     */
    static anyNotValid(...args) {
        for (const aIndex in args) {
            if (!EzObject.isValid(args[aIndex])) {
                return true;
            }
        }

        return false;
    }

    /**
     * @static
     * @public @method
     * Determines if the provided value is a type of the provided type string.
     * @param {any} value
     * @param {string} typeName
     * @returns {boolean}
     */
    static isTypeOf(value, typeName) {
        if (!EzObject.isValid(typeName) || 'string' !== typeof typeName) {
            throw new EzBadParamException(
                'typeName',
                EzObject,
                EzObject.isTypeOf);
        }

        return typeName === typeof value;
    }

    /**
     * @static
     * @public @method
     * Determines if the provided object has a direct property propertyName.
     * Returns false if the object is undefined or null.
     * Returns false if the property is inherited.
     * To check direct AND inherited properties, use EzObject.hasProperty()
     * @param {any} aObject
     * @param {string} propertyName
     * @returns {boolean}
     */
    static hasOwnProperty(aObject, propertyName) {
        if (!EzObject.isValid(aObject)) {
            throw new EzBadParamException(
                'aObject',
                EzObject,
                EzObject.hasOwnProperty);
        }
        if (!EzObject.isValid(propertyName) || !EzObject.isTypeOf(propertyName, 'string') || 0 === propertyName.length) {
            throw new EzBadParamException(
                'propertyName',
                EzObject.ezTypeName,
                'ezHasOwnProperty(objectRef, propertyName)');
        }

        return Object.hasOwn(aObject, propertyName);
    }

    /**
     * @static
     * @public @method
     * Determines if the object has a property propertyName. Returns null if the objectRef is false, the
     * @param {any} aObject
     * @param {string} propertyName
     * @returns {boolean}
     */
    static hasProperty(aObject, propertyName) {
        if (!EzObject.isValid(aObject)) {
            throw new EzBadParamException(
                'aObject',
                EzObject,
                EzObject.hasOwnProperty);
        }
        if (!EzObject.isValid(propertyName) || !EzObject.isTypeOf(propertyName, 'string') || 0 === propertyName.length) {
            throw new EzBadParamException(
                'propertyName',
                EzObject,
                'ezHasOwnProperty(objectRef, propertyName)');
        }

        return propertyName in aObject;
    }

    /**
     * @static
     * @public @method
     * Clones the properties from aObjectToClone into a new object by stringifying the object to json
     * and then parsing the JSON string back into an object.
     * In some cases, stringifying the object might fail. If this happens, then the normal 'clone' is
     * called which will use Javascript's structuredClone(...) method.
     * @param {any} aObjectToClone
     * @returns {object}
     */
    static cloneProperties(aObjectToClone) {
        if (!EzObject.isValid(aObjectToClone)) {
            return null;
        }

        try {
            return structuredClone(aObjectToClone);
        } catch (err) {
            globalThis?.console.warn(
                'Failed to clone the properties of an object using JSON.stringify and JSON.parse ' +
                `due to the following error: ${err.message}` +
                ' Retrying with EzObject.clone(...) which will leverage Javascript\'s structedClode(..) to perform the clone.');

            return EzObject.cone(aObjectToClone);
        }
    }

    /**
     * @static
     * @public @method
     * Executes a deep clone of an object
     * @param {any} objectToClone
     * @returns {object}
     */
    static clone(aObjectToClone) {
        if (!EzObject.isValid(aObjectToClone)) {
            return null;
        }

        try {
            return structuredClone(aObjectToClone);
        } catch (err) {
            globalThis?.console.warn(
                `Failed to clone object with Javascript's structuredClone(...) method due to the following error: ${err.message}`);
        }

        return undefined;
    }

    /**
     * @static
     * @public @method
     * Attempts to determine if the provided aObject is a valid MomentJS moment instance or not.
     * @param {any} aObject
     * @returns {boolean}
     */
    static isValidMoment(aObject) {
        return EzObject.isValid(aObject) && aObject?._isAMomentObject && aObject?._isValid;
    }
}
